import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useScroll,
  useTransform,
  useSpring,
} from "framer-motion";
import axios from "axios";
import {
  Box,
  Container,
  Paper,
  Stack,
  Grid,
  Typography,
  Button,
  IconButton,
  Chip,
  Avatar,
  AvatarGroup,
  Tooltip,
  Link,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import {
  FaArrowRight,
  FaCopy,
  FaCheckCircle,
  FaSchool,
  FaUserGraduate,
  FaHeadset,
  FaApple,
  FaPlayCircle,
  FaLaptopCode,
  FaStar,
  FaEye,
  FaEnvelope,
  FaWhatsapp,
  FaPlay,
  FaTimes,
  FaDownload,
} from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import { API_URL } from "../config";
import Slider from "../components/Slider";
import PageLoader from "../components/pageLoader";
import {
  fadeUp,
  fadeIn,
  fadeLeft,
  scaleIn,
  heroReveal,
  staggerContainer,
  staggerContainerFast,
  staggerItem,
  staggerItemSoft,
  staggerItemScale,
} from "../utils/useScrollAnimation";
import BackToTop from "../components/backToTop";
import { ServerDialog } from "../components/dialog/serverDialog";

// ─── Theme ────────────────────────────────────────────────────────────────────
const landingTheme = createTheme({
  typography: {
    fontFamily: "'Bricolage Grotesque', sans-serif",
    h1: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 900 },
    h2: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 900 },
    h3: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 900 },
    h4: { fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800 },
  },
  palette: { primary: { main: "#0288D1" } },
});

// ─── Injected CSS ─────────────────────────────────────────────────────────────
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,300;12..96,400;12..96,500;12..96,600;12..96,700;12..96,800;12..96,900&display=swap');

  @keyframes gradShift    { 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
  @keyframes orbFloat     { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(28px,-18px) scale(1.05)} 66%{transform:translate(-14px,12px) scale(.96)} }
  @keyframes scanSweep    { 0%{transform:translateY(-100%) scaleX(1)} 100%{transform:translateY(400%) scaleX(1)} }
  @keyframes floatY       { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
  @keyframes floatY2      { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
  @keyframes ringPop      { 0%{transform:scale(.4);opacity:0} 60%{transform:scale(1.2);opacity:.6} 100%{transform:scale(1);opacity:1} }
  @keyframes shimmerPass  { 0%{left:-100%} 100%{left:200%} }
  @keyframes countIn      { 0%{opacity:0;transform:translateY(14px) scale(.8)} 100%{opacity:1;transform:translateY(0) scale(1)} }
  @keyframes borderPulse  { 0%,100%{border-color:rgba(2,136,209,.15)} 50%{border-color:rgba(2,136,209,.55)} }
  @keyframes particleRise { 0%{opacity:0;transform:translateY(0) rotate(0)} 8%{opacity:1} 100%{opacity:0;transform:translateY(-90px) rotate(360deg)} }
  @keyframes rotateSlow   { to{transform:rotate(360deg)} }
  @keyframes glowBreath   { 0%,100%{opacity:.5;box-shadow:0 0 10px currentColor} 50%{opacity:1;box-shadow:0 0 22px currentColor} }
  @keyframes heroUnderline{ from{width:0;opacity:0} to{width:90px;opacity:1} }
  @keyframes marquee      { 0%{transform:translateX(0)} 100%{transform:translateX(-50%)} }
  @keyframes blink        { 0%,100%{opacity:1} 50%{opacity:0} }
  @keyframes cardReveal   { from{opacity:0;transform:translateY(24px) scale(.96)} to{opacity:1;transform:translateY(0) scale(1)} }
  @keyframes ripple       { to{transform:scale(2.5);opacity:0} }

  .word-reveal { display:inline-block; margin-right:.28em; will-change:transform,opacity; }
  .cursor      { display:inline-block; animation:blink 1s step-end infinite; }

  ::-webkit-scrollbar { width:5px }
  ::-webkit-scrollbar-track { background:transparent }
  ::-webkit-scrollbar-thumb { background:linear-gradient(180deg,#0288D1,#7c3aed); border-radius:99px }
`;

// ─── Motion ───────────────────────────────────────────────────────────────────
const MB = motion(Box);
const MP = motion(Paper);
const vp = (a = 0.12) => ({ once: true, amount: a });

// ─── Reusables ────────────────────────────────────────────────────────────────
const SectionChip = ({ label, color = "#0288D1", bg }) => (
  <Chip
    label={label}
    size="small"
    sx={{
      mb: 2,
      fontWeight: 700,
      fontFamily: "'Bricolage Grotesque', sans-serif",
      fontSize: "0.68rem",
      letterSpacing: "1px",
      textTransform: "uppercase",
      background: bg || `${color}15`,
      color,
      border: `1px solid ${color}35`,
      borderRadius: "7px",
    }}
  />
);

const SectionDivider = ({ color = "#0288D1" }) => (
  <Box sx={{ display: "flex", alignItems: "center", my: { xs: 5, md: 7 } }}>
    <Box
      sx={{
        flex: 1,
        height: 1,
        background: `linear-gradient(90deg, transparent, ${color}35)`,
      }}
    />
    <Box sx={{ mx: 2, position: "relative" }}>
      <Box
        sx={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: color,
          color,
          animation: "glowBreath 2.5s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: -4,
          borderRadius: "50%",
          border: `1px solid ${color}50`,
          animation: "ringPop 2s ease-in-out infinite",
        }}
      />
    </Box>
    <Box
      sx={{
        flex: 1,
        height: 1,
        background: `linear-gradient(90deg, ${color}35, transparent)`,
      }}
    />
  </Box>
);

const Particle = ({ delay, left, size, color }) => (
  <Box
    sx={{
      position: "absolute",
      bottom: "8%",
      left,
      width: size,
      height: size,
      borderRadius: "50%",
      background: color,
      opacity: 0,
      animation: `particleRise ${
        2.5 + Math.random()
      }s ease-in ${delay}s infinite`,
      pointerEvents: "none",
      zIndex: 0,
    }}
  />
);

// ─── Word-by-word reveal ──────────────────────────────────────────────────────
const WordReveal = ({ text, delay = 0 }) => (
  <Box sx={{ textAlign: "center", mb: 1 }}>
    {text.split(" ").map((word, i) => (
      <motion.span
        key={i}
        className="word-reveal"
        initial={{ opacity: 0, y: 44, rotateX: -90, filter: "blur(4px)" }}
        whileInView={{ opacity: 1, y: 0, rotateX: 0, filter: "blur(0px)" }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{
          delay: delay + i * 0.1,
          duration: 0.65,
          ease: [0.22, 1, 0.36, 1],
        }}
        style={{
          fontWeight: 900,
          color: "#0c1a2e",
          letterSpacing: "-1px",
          fontFamily: "'Bricolage Grotesque', sans-serif",
          fontSize: "clamp(2rem, 4vw, 2.8rem)",
          transformOrigin: "top center",
        }}
      >
        {word}
      </motion.span>
    ))}
  </Box>
);

// ─── Count-up ─────────────────────────────────────────────────────────────────
const useCountUp = (end, dur = 2000) => {
  const [count, setCount] = useState(0);
  const [go, setGo] = useState(false);
  const start = useCallback(() => {
    if (!go) setGo(true);
  }, [go]);
  useEffect(() => {
    if (!go) return;
    const n = parseFloat(end.toString().replace(/[^0-9.]/g, ""));
    if (!n) return;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const p = Math.min((now - t0) / dur, 1);
      setCount(Math.floor(ease(p) * n));
      if (p < 1) requestAnimationFrame(tick);
      else setCount(n);
    };
    requestAnimationFrame(tick);
  }, [go, end, dur]);
  return { count, start };
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ stat, index }) => {
  const { ref, inView } = useInView({ threshold: 0.4, triggerOnce: true });
  const { count, start } = useCountUp(stat.value, 1800 + index * 180);
  useEffect(() => {
    if (inView) start();
  }, [inView, start]);
  const fmt = (raw, cur) => {
    const s = raw.toString();
    if (s.includes("K")) return `${cur}K+`;
    if (s.includes("+")) return `${cur}+`;
    if (s.includes("/")) return raw;
    return cur.toString();
  };
  return (
    <motion.div
      ref={ref}
      variants={staggerItemScale}
      style={{ height: "100%" }}
    >
      <Paper
        elevation={0}
        sx={{
          p: 3.5,
          borderRadius: "22px",
          textAlign: "center",
          background: stat.bg,
          border: `1.5px solid ${stat.border}`,
          height: "100%",
          position: "relative",
          overflow: "hidden",
          transition: "all 0.32s cubic-bezier(.4,0,.2,1)",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: "-100%",
            width: "60%",
            height: "100%",
            background: `linear-gradient(90deg,transparent,${stat.accent}18,transparent)`,
            animation: inView ? "shimmerPass 1.8s ease .5s 1 both" : "none",
          },
          "&:hover": {
            transform: "translateY(-10px) scale(1.02)",
            boxShadow: `0 28px 56px ${stat.accent}24`,
            borderColor: stat.accent,
          },
        }}
      >
        {/* Ring + icon */}
        <Box
          sx={{
            position: "relative",
            width: 64,
            height: 64,
            mx: "auto",
            mb: 3,
          }}
        >
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              borderRadius: "50%",
              border: `2px solid ${stat.accent}35`,
              animation: inView
                ? `ringPop 1s ease ${index * 0.15}s both`
                : "none",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: -8,
              borderRadius: "50%",
              border: `1px dashed ${stat.accent}20`,
              animation: "rotateSlow 12s linear infinite",
            }}
          />
          <Box
            sx={{
              width: "100%",
              height: "100%",
              borderRadius: "18px",
              background: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 8px 24px ${stat.accent}22`,
              position: "relative",
              zIndex: 1,
              animation: inView
                ? `countIn .65s cubic-bezier(.34,1.56,.64,1) ${
                    0.3 + index * 0.1
                  }s both`
                : "none",
            }}
          >
            {stat.icon}
          </Box>
        </Box>
        {/* Number */}
        <Typography
          sx={{
            fontWeight: 900,
            fontSize: "2.8rem",
            lineHeight: 1,
            letterSpacing: "-2.5px",
            fontFamily: "'Bricolage Grotesque', sans-serif",
            background: inView
              ? `linear-gradient(135deg, #0c1a2e 0%, ${stat.accent} 100%)`
              : "none",
            WebkitBackgroundClip: inView ? "text" : "unset",
            WebkitTextFillColor: inView ? "transparent" : "#0c1a2e",
            animation: inView
              ? `countIn .5s ease ${0.2 + index * 0.15}s both`
              : "none",
          }}
        >
          {fmt(stat.value, count)}
        </Typography>
        {/* Accent bar */}
        <Box
          sx={{
            height: 3.5,
            borderRadius: 2,
            mx: "auto",
            mt: 1.5,
            mb: 1.2,
            background: `linear-gradient(90deg,${stat.accent},${stat.accent}40)`,
            width: inView ? "58%" : "0%",
            transition: `width .9s cubic-bezier(.4,0,.2,1) ${
              0.65 + index * 0.15
            }s`,
          }}
        />
        <Typography
          sx={{
            color: "#64748b",
            fontWeight: 700,
            fontSize: "0.82rem",
            fontFamily: "'Bricolage Grotesque', sans-serif",
          }}
        >
          {stat.label}
        </Typography>
      </Paper>
    </motion.div>
  );
};

// ─── Video modal ──────────────────────────────────────────────────────────────
const VideoModal = ({ videoId, title, onClose }) => (
  <AnimatePresence>
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.88)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.85, opacity: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 28 }}
        style={{
          width: "100%",
          maxWidth: 900,
          borderRadius: 20,
          overflow: "hidden",
          position: "relative",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Box sx={{ position: "absolute", top: 12, right: 12, zIndex: 1 }}>
          <IconButton
            onClick={onClose}
            sx={{
              background: "rgba(255,255,255,0.12)",
              color: "#fff",
              backdropFilter: "blur(8px)",
              "&:hover": { background: "rgba(255,255,255,0.22)" },
            }}
          >
            <FaTimes size={16} />
          </IconButton>
        </Box>
        <Box
          sx={{
            paddingTop: "56.25%",
            position: "relative",
            background: "#000",
          }}
        >
          <iframe
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1`}
            title={title}
            frameBorder="0"
            allowFullScreen
            allow="autoplay"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
            }}
          />
        </Box>
      </motion.div>
    </motion.div>
  </AnimatePresence>
);

// ─── Marquee strip ────────────────────────────────────────────────────────────
const MarqueeStrip = ({ items }) => {
  const doubled = [...items, ...items];
  return (
    <Box
      sx={{
        overflow: "hidden",
        background: "rgba(2,136,209,0.04)",
        borderTop: "1px solid rgba(2,136,209,0.1)",
        borderBottom: "1px solid rgba(2,136,209,0.1)",
        py: 1.5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 0,
          animation: "marquee 20s linear infinite",
          width: "max-content",
        }}
      >
        {doubled.map((item, i) => (
          <Box
            key={i}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 3.5,
              borderRight: "1px solid rgba(2,136,209,0.15)",
              whiteSpace: "nowrap",
            }}
          >
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "#0288D1",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: "#0288D1",
                fontFamily: "'Bricolage Grotesque', sans-serif",
                letterSpacing: "0.3px",
              }}
            >
              {item}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ─── Scroll progress bar ──────────────────────────────────────────────────────
const ScrollProgress = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });
  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        zIndex: 9998,
        transformOrigin: "0%",
      }}
    >
      <motion.div
        style={{
          height: "100%",
          scaleX,
          background: "linear-gradient(90deg, #0288D1, #0ea5e9, #7c3aed)",
          transformOrigin: "0%",
        }}
      />
    </Box>
  );
};

// ─── Main ─────────────────────────────────────────────────────────────────────
const LandingPage = () => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("md"));
  // const [desktopLinks, setDesktopLinks] = useState({ win: null, linux: null });
  const [loaderDone, setLoaderDone] = useState(
    () => !!sessionStorage.getItem("loaded")
  );
  const [pageVisible, setPageVisible] = useState(
    () => !!sessionStorage.getItem("loaded")
  );
  const [students, setStudents] = useState([]);
  const [copySuccess, setCopySuccess] = useState("");
;
  const [activeVideo, setActiveVideo] = useState(null);
  const [open, setOpen] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress: heroProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(heroProgress, [0, 1], ["0%", "25%"]);
  const heroOpacity = useTransform(heroProgress, [0, 0.7], [1, 0]);
  const [desktopLinks, setDesktopLinks] = useState({ win: null, linux: null })
  const [loading, setLoading] = useState(false);
  console.log(setLoading)
  const getDesktopDownloadLink = () => {
    const ua = navigator.userAgent;

    if (/Win/i.test(ua)) {
      return "https://github.com/UC-BLACK1/Liteupper-WAEC-Desktop/releases/download/v0.1.2/LiteUpper-institution-student.Setup.0.1.0.exe";
    }

    if (/Mac/i.test(ua)) {
      if (/arm64|iPhone|iPad/i.test(ua)) {
        return "https://github.com/UC-BLACK1/Liteupper-WAEC-Desktop/releases/download/v0.1.2/LiteUpper-0.1.0-arm64.dmg";
      }

      return "https://github.com/UC-BLACK1/Liteupper-WAEC-Desktop/releases/download/v0.1.2/LiteUpper-0.1.0-x86_64.dmg";
    }

    if (/Linux/i.test(ua)) {
      return "https://github.com/UC-BLACK1/Liteupper-WAEC-Desktop/releases/download/v0.1.2/LiteUpper-institution-0.1.0.AppImage";
    }

    return null;
  };

  const handleDesktopDownload = () => {
    const link = getDesktopDownloadLink();

    if (link) {
      window.open(link, "_blank");
    } else {
      alert("Your operating system is not supported.");
    }
  };

   const downloadLink = useMemo(() => {
     const ua = navigator.userAgent;
     if (/Win/i.test(ua)) return desktopLinks.win;
     if (/Linux/i.test(ua)) return desktopLinks.linux;
     return null;
   }, [desktopLinks]);

   useEffect(() => {
     axios
       .get(`${API_URL}/desktop-app/`)
       .then((r) =>
         setDesktopLinks({
           win: r.data.DESKTOP_APP_WIN,
           linux: r.data.DESKTOP_APP_LINUX,
         })
         
       )
       .catch(console.error);
   }, []);

  const handleDownloadClick = () => {
    if (downloadLink) {
      window.location.href = downloadLink; // Or window.open(downloadLink, '_blank')
    } else {
      alert("Your operating system is not supported for desktop downloads.");
    }
  };



  useEffect(() => {
    axios
      .get(`${API_URL}/institution-students/`)
      .then((r) => setStudents(r.data.slice(0, 12)))
      .catch(console.error);
  }, []);

  const copyProposalLink = () => {
    navigator.clipboard.writeText(
      `${window.location.origin}/LiteupperProposal`
    );
    setCopySuccess("Copied!");
    setTimeout(() => setCopySuccess(""), 2500);
  };

  const handleDone = useCallback(() => {
    sessionStorage.setItem("loaded", "1");
    setLoaderDone(true);
    setTimeout(() => setPageVisible(true), 60);
  }, []);

  const STATS = [
    {
      icon: <FaSchool size={22} color="#0288D1" />,
      value: `${students.length || 20}+`,
      label: "Partner Schools",
      bg: "rgba(2,136,209,0.06)",
      border: "rgba(2,136,209,0.16)",
      accent: "#0288D1",
    },
    {
      icon: <FaUserGraduate size={22} color="#7c3aed" />,
      value: "10K+",
      label: "Students",
      bg: "rgba(124,58,237,0.06)",
      border: "rgba(124,58,237,0.16)",
      accent: "#7c3aed",
    },
    {
      icon: <FaCheckCircle size={22} color="#059669" />,
      value: "5+",
      label: "Exam Bodies",
      bg: "rgba(5,150,105,0.06)",
      border: "rgba(5,150,105,0.16)",
      accent: "#059669",
    },
    {
      icon: <FaHeadset size={22} color="#d97706" />,
      value: "24/7",
      label: "24/7 Support",
      bg: "rgba(217,119,6,0.06)",
      border: "rgba(217,119,6,0.16)",
      accent: "#d97706",
    },
  ];

  const GOALS = [
    {
      text: "Make exam preparation accessible and effective for all students",
      icon: <FaUserGraduate size={18} color="#0288D1" />,
      bg: "rgba(2,136,209,0.06)",
      border: "rgba(2,136,209,0.14)",
      accent: "#0288D1",
    },
    {
      text: "Provide curated resources aligned with JAMB, WAEC, NECO and more",
      icon: <FaCheckCircle size={18} color="#7c3aed" />,
      bg: "rgba(124,58,237,0.06)",
      border: "rgba(124,58,237,0.14)",
      accent: "#7c3aed",
    },
    {
      text: "Help students build confidence and improve success rates",
      icon: <FaStar size={18} color="#d97706" />,
      bg: "rgba(217,119,6,0.06)",
      border: "rgba(217,119,6,0.14)",
      accent: "#d97706",
    },
    {
      text: "Support teachers with digital tools and analytics",
      icon: <FaLaptopCode size={18} color="#059669" />,
      bg: "rgba(5,150,105,0.06)",
      border: "rgba(5,150,105,0.14)",
      accent: "#059669",
    },
    {
      text: "Promote digital literacy in African education",
      icon: <FaSchool size={18} color="#ef4444" />,
      bg: "rgba(239,68,68,0.06)",
      border: "rgba(239,68,68,0.14)",
      accent: "#ef4444",
    },
  ];

  const VIDEOS = [
    {
      title: "Platform Overview",
      id: "f3Asg5Pr5Po",
      thumb: "https://www.youtube.com/watch?v=f3Asg5Pr5Po",
    },

    // {
    //   title: "Full Feature Demo",
    //   id: "ktx4yYWTSeo",
    //   thumb: "https://img.youtube.com/vi/ktx4yYWTSeo/maxresdefault.jpg",
    // },
  ];

  const MARQUEE_ITEMS = [
    "JAMB",
    "WAEC",
    "NECO",
    "Post-UTME",
    "CBT Platform",
    "Smart Analytics",
    "Past Questions",
    "Digital Assessments",
    "Class Management",
    "24/7 Support",
  ];

  // ──────────────────────────────────────────────────────────────────────────
  return (
    <ThemeProvider theme={landingTheme}>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />
      <ScrollProgress />

      {activeVideo && (
        <VideoModal
          videoId={activeVideo.id}
          title={activeVideo.title}
          onClose={() => setActiveVideo(null)}
        />
      )}

      <Box
        sx={{
          overflowX: "hidden",
          position: "relative",
          fontFamily: "'Bricolage Grotesque', sans-serif",
        }}
      >
        <Box
          sx={{
            opacity: pageVisible ? 1 : 0,
            transform: pageVisible ? "translateY(0)" : "translateY(16px)",
            transition:
              "opacity 0.9s cubic-bezier(.4,0,.2,1), transform 0.9s cubic-bezier(.4,0,.2,1)",
            pointerEvents: pageVisible ? "all" : "none",
          }}
        >
          <Slider />

          {/* ══════════════ HERO CTA ══════════════ */}
          <Box
            ref={heroRef}
            sx={{
              background:
                "linear-gradient(140deg, #010a1a 0%, #012d6b 30%, #0277bd 60%, #0ea5e9 85%, #38bdf8 100%)",
              py: { xs: 14, md: 20 },
              px: { xs: 2, md: 4 },
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Parallax layer */}
            <motion.div
              style={{
                y: heroY,
                opacity: heroOpacity,
                position: "absolute",
                inset: 0,
                pointerEvents: "none",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0.04,
                  backgroundImage:
                    "radial-gradient(circle, #fff 1px, transparent 1px)",
                  backgroundSize: "44px 44px",
                }}
              />
              {/* Orbs */}
              {[
                {
                  top: "-18%",
                  left: "-10%",
                  size: 600,
                  color: "rgba(2,136,209,0.2)",
                  dur: "16s",
                },
                {
                  bottom: "-12%",
                  right: "-6%",
                  size: 500,
                  color: "rgba(124,58,237,0.16)",
                  dur: "20s",
                  rev: true,
                },
                {
                  top: "30%",
                  left: "55%",
                  size: 300,
                  color: "rgba(14,165,233,0.12)",
                  dur: "13s",
                },
              ].map((o, i) => (
                <Box
                  key={i}
                  sx={{
                    position: "absolute",
                    top: o.top,
                    bottom: o.bottom,
                    left: o.left,
                    right: o.right,
                    width: o.size,
                    height: o.size,
                    borderRadius: "50%",
                    background: `radial-gradient(circle, ${o.color} 0%, transparent 70%)`,
                    animation: `orbFloat ${o.dur} ease-in-out ${
                      o.rev ? "infinite reverse" : "infinite"
                    }`,
                    pointerEvents: "none",
                  }}
                />
              ))}
            </motion.div>

            {/* Scanline */}
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                height: 280,
                background:
                  "linear-gradient(to bottom, transparent, rgba(255,255,255,0.016), transparent)",
                animation: "scanSweep 5s linear infinite",
                pointerEvents: "none",
                zIndex: 1,
              }}
            />

            {/* Particles */}
            {["6%", "16%", "28%", "42%", "56%", "70%", "84%", "94%"].map(
              (l, i) => (
                <Particle
                  key={i}
                  delay={i * 0.6}
                  left={l}
                  size={i % 2 ? 5 : 7}
                  color={
                    i % 3 === 0
                      ? "rgba(14,165,233,0.7)"
                      : i % 3 === 1
                      ? "rgba(124,58,237,0.6)"
                      : "rgba(56,189,248,0.5)"
                  }
                />
              )
            )}

            <Box sx={{ position: "relative", zIndex: 2 }}>
              <MB
                variants={fadeIn}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.3)}
              >
                <Chip
                  label="🚀 CBT Platform for Schools & Students"
                  size="small"
                  sx={{
                    mb: 4,
                    fontWeight: 700,
                    fontSize: "0.74rem",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    letterSpacing: "0.6px",
                    background: "rgba(255,255,255,0.1)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.24)",
                    borderRadius: "9px",
                    backdropFilter: "blur(12px)",
                  }}
                />
              </MB>

              <MB
                variants={heroReveal}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.3)}
                transition={{ delay: 0.1 }}
              >
                <Typography
                  sx={{
                    fontWeight: 900,
                    color: "#fff",
                    letterSpacing: "-2.5px",
                    mb: 0.5,
                    fontSize: { xs: "3rem", sm: "4rem", md: "5.2rem" },
                    lineHeight: 1,
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                  }}
                >
                  Smarter Exams.
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 900,
                    fontSize: { xs: "3rem", sm: "4rem", md: "5.2rem" },
                    letterSpacing: "-2.5px",
                    lineHeight: 1,
                    mb: 4,
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    background:
                      "linear-gradient(135deg, #7dd3fc 0%, #a5b4fc 50%, #f0abfc 100%)",
                    backgroundSize: "200% 200%",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    animation: "gradShift 5s ease infinite",
                  }}
                >
                  Better Results.
                </Typography>
              </MB>

              {/* Animated underline */}
              <motion.div
                initial={{ width: 0, opacity: 0 }}
                whileInView={{ width: 90, opacity: 1 }}
                viewport={{ once: true }}
                transition={{
                  delay: 0.55,
                  duration: 0.9,
                  ease: [0.22, 1, 0.36, 1],
                }}
                style={{
                  height: 5,
                  borderRadius: 99,
                  background:
                    "linear-gradient(90deg, #0ea5e9, #a78bfa, #f0abfc)",
                  margin: "0 auto 28px",
                  boxShadow: "0 0 20px rgba(167,139,250,0.5)",
                }}
              />

              <MB
                variants={fadeUp}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.3)}
                transition={{ delay: 0.32 }}
              >
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.65)",
                    fontSize: { xs: "1.02rem", md: "1.22rem" },
                    mb: 7,
                    maxWidth: 600,
                    mx: "auto",
                    lineHeight: 1.82,
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    fontWeight: 400,
                  }}
                >
                  LiteUpper powers digital assessments for institutions and
                  helps students ace JAMB, WAEC, NECO and more — with curated
                  past questions and smart analytics.
                </Typography>
              </MB>

              <MB
                variants={staggerContainerFast(0.14, 0.52)}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.3)}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  justifyContent="center"
                  alignItems="center"
                >
                  {[
                    {
                      label: "Get Started Free",
                      icon: <FaArrowRight size={14} />,
                      onClick: () => navigate("/login"),
                      contained: true,
                      sx: {
                        background: "#fff",
                        color: "#0277bd",
                        fontWeight: 900,
                        boxShadow: "0 10px 36px rgba(0,0,0,0.28)",
                        "&:hover": {
                          background: "#f0f9ff",
                          transform: "translateY(-4px)",
                          boxShadow: "0 20px 44px rgba(0,0,0,0.32)",
                        },
                      },
                    },
                    {
                      label: "Download CBT App for Students",
                      icon: <FaDownload size={14} />,
                      onClick: handleDesktopDownload ,
                      contained: false,
                      sx: {
                        borderColor: "rgba(255,255,255,0.38)",
                        color: "#fff",
                        fontWeight: 700,
                        backdropFilter: "blur(12px)",
                        background: "rgba(255,255,255,0.07)",
                        "&:hover": {
                          borderColor: "#fff",
                          background: "rgba(255,255,255,0.15)",
                          transform: "translateY(-4px)",
                        },
                      },
                    },

                    {
                      label: "View Proposal",
                      onClick: () => navigate("/LiteupperProposal"),
                      contained: false,
                      sx: {
                        borderColor: "rgba(255,255,255,0.38)",
                        color: "#fff",
                        fontWeight: 700,
                        backdropFilter: "blur(12px)",
                        background: "rgba(255,255,255,0.07)",
                        "&:hover": {
                          borderColor: "#fff",
                          background: "rgba(255,255,255,0.15)",
                          transform: "translateY(-4px)",
                        },
                      },
                    },
                  ].map((btn) => (
                    <motion.div key={btn.label} variants={staggerItemSoft}>
                      <Button
                        variant={btn.contained ? "contained" : "outlined"}
                        size="large"
                        onClick={btn.onClick}
                        endIcon={btn.icon}
                        sx={{
                          px: 5.5,
                          py: 2,
                          borderRadius: "14px",
                          fontFamily: "'Bricolage Grotesque', sans-serif",
                          fontSize: "1rem",
                          textTransform: "none",
                          transition: "all 0.3s",
                          ...btn.sx,
                        }}
                      >
                        {btn.label}
                      </Button>
                    </motion.div>
                  ))}
                </Stack>
              </MB>
            </Box>
          </Box>

          {/* Marquee */}
          <MarqueeStrip items={MARQUEE_ITEMS} />

          <Container maxWidth="lg" sx={{ py: { xs: 10, md: 14 } }}>
            {/* ══════════════ SCHOOLS BANNER ══════════════ */}
            <MP
              variants={fadeLeft}
              initial="hidden"
              whileInView="show"
              viewport={vp(0.12)}
              elevation={0}
              sx={{
                p: { xs: 3.5, md: 5 },
                borderRadius: "24px",
                mb: 14,
                background:
                  "linear-gradient(135deg, rgba(2,136,209,0.07), rgba(14,165,233,0.03))",
                border: "1.5px solid rgba(2,136,209,0.15)",
                boxShadow: "0 6px 40px rgba(2,136,209,0.08)",
                position: "relative",
                overflow: "hidden",
                animation: "borderPulse 4s ease-in-out infinite",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 1.5,
                  background:
                    "linear-gradient(90deg, transparent, rgba(2,136,209,0.6), transparent)",
                }}
              />
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems="center"
                justifyContent="space-between"
                gap={3}
              >
                <Stack direction="row" alignItems="center" spacing={2.5}>
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: "16px",
                      background: "linear-gradient(135deg, #0277bd, #0ea5e9)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0 8px 20px rgba(2,136,209,0.38)",
                      flexShrink: 0,
                      animation: "floatY2 4s ease-in-out infinite",
                    }}
                  >
                    <FaSchool size={26} color="#fff" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: "#0c1a2e",
                        fontSize: "1.1rem",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        letterSpacing: "-0.3px",
                      }}
                    >
                      For Schools & Institutions
                    </Typography>
                    <Typography
                      sx={{
                        color: "#64748b",
                        fontSize: "0.86rem",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        mt: 0.3,
                      }}
                    >
                      Full CBT solution with analytics, class management and
                      reporting
                    </Typography>
                  </Box>
                </Stack>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  flexShrink={0}
                >
                  <Button
                    variant="contained"
                    onClick={() => navigate("/LiteupperProposal")}
                    endIcon={<FaArrowRight size={12} />}
                    sx={{
                      borderRadius: "12px",
                      textTransform: "none",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontWeight: 700,
                      px: 2.8,
                      fontSize: "0.88rem",
                      background: "linear-gradient(135deg, #0288D1, #0ea5e9)",
                      boxShadow: "0 4px 18px rgba(2,136,209,0.35)",
                      "&:hover": {
                        background: "linear-gradient(135deg, #0277bd, #0288D1)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    Full Proposal
                  </Button>
                  <Tooltip title="Copy proposal link" arrow>
                    <IconButton
                      onClick={copyProposalLink}
                      sx={{
                        color: "#0288D1",
                        background: "rgba(2,136,209,0.08)",
                        border: "1px solid rgba(2,136,209,0.2)",
                        borderRadius: "12px",
                        "&:hover": { background: "rgba(2,136,209,0.16)" },
                      }}
                    >
                      <FaCopy size={15} />
                    </IconButton>
                  </Tooltip>
                  <AnimatePresence>
                    {copySuccess && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8, x: -8 }}
                        animate={{ opacity: 1, scale: 1, x: 0 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                      >
                        <Chip
                          label={copySuccess}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            background: "rgba(5,150,105,0.12)",
                            color: "#059669",
                            border: "1px solid rgba(5,150,105,0.25)",
                            borderRadius: "9px",
                          }}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Stack>
              </Stack>
            </MP>

            {/* ══════════════ ABOUT ══════════════ */}
            <Box id="about" sx={{ mb: 18 }}>
              <Stack alignItems="center" mb={9}>
                <SectionChip label="About Us" color="#0288D1" />
                <WordReveal text="What is LiteUpper?" />
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  whileInView={{ width: 70, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                >
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 99,
                      background: "linear-gradient(90deg, #0288D1, #7c3aed)",
                      mx: "auto",
                      mt: 2,
                      mb: 3.5,
                      boxShadow: "0 0 14px rgba(2,136,209,0.4)",
                    }}
                  />
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, y: 22 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{
                    delay: 0.45,
                    duration: 0.7,
                    ease: [0.22, 1, 0.36, 1],
                  }}
                >
                  <Typography
                    sx={{
                      color: "#64748b",
                      textAlign: "center",
                      maxWidth: 640,
                      lineHeight: 1.92,
                      fontSize: "1.04rem",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    LiteUpper is a powerful class-based CBT platform that
                    enables schools and training centres to conduct seamless,
                    secure, and efficient digital assessments through both
                    online and offline examination modes. The platform is
                    designed to support institutions even in areas with limited
                    or unstable internet connectivity, ensuring uninterrupted
                    examinations and reliable performance tracking. Beyond
                    institutions, LiteUpper empowers students preparing for
                    external examinations with a comprehensive collection of
                    verified past questions and answers for WAEC, JAMB, NECO,
                    GCE, and Post-UTME preparation.
                  </Typography>
                </motion.div>
              </Stack>

              <MB
                variants={staggerContainer(0.14, 0.05)}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.08)}
              >
                <Grid container spacing={3}>
                  {STATS.map((s, i) => (
                    <Grid item xs={6} md={3} key={i}>
                      <StatCard stat={s} index={i} />
                    </Grid>
                  ))}
                </Grid>
              </MB>
            </Box>

            <SectionDivider color="#0288D1" />

            {/* ══════════════ DOWNLOAD ══════════════ */}
            <Box sx={{ mb: 18 }}>
              <Stack alignItems="center" mb={9}>
                <SectionChip label="Download" color="#059669" />
                <WordReveal text="Get the App Today" />
                <motion.div
                  initial={{ width: 0, opacity: 0 }}
                  whileInView={{ width: 70, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                >
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 99,
                      background: "linear-gradient(90deg, #059669, #10b981)",
                      mx: "auto",
                      mt: 2,
                      mb: 2,
                    }}
                  />
                </motion.div>
                <Typography
                  sx={{
                    color: "#64748b",
                    fontFamily: "'Bricolage Grotesque', sans-serif",
                    fontSize: "1rem",
                  }}
                >
                  Available on
                </Typography>
              </Stack>

              <MB
                variants={staggerContainer(0.18, 0.05)}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.12)}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={3}
                  justifyContent="center"
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <motion.div variants={staggerItemSoft}>
                    <Link
                      href="https://play.google.com/store/apps/details?id=com.liteupper.liteupper"
                      target="_blank"
                      rel="noopener"
                      style={{ textDecoration: "none" }}
                    >
                      <Box
                        component="img"
                        src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                        alt="Google Play"
                        sx={{
                          height: 68,
                          transition: "all 0.28s",
                          "&:hover": {
                            transform: "scale(1.08) translateY(-4px)",
                            filter: "drop-shadow(0 14px 22px rgba(0,0,0,0.22))",
                          },
                        }}
                      />
                    </Link>
                  </motion.div>
                  <motion.div variants={staggerItemSoft}>
                    <Paper
                      elevation={0}
                      sx={{
                        px: 3.5,
                        py: 2,
                        borderRadius: "16px",
                        border: "1px solid #e2e8f0",
                        background: "linear-gradient(135deg, #f8fafc, #fff)",
                        display: "flex",
                        alignItems: "center",
                        gap: 1.8,
                      }}
                    >
                      <FaApple size={28} color="#64748b" />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#94a3b8",
                            fontWeight: 700,
                            textTransform: "uppercase",
                            letterSpacing: "0.8px",
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                          }}
                        >
                          Coming Soon
                        </Typography>
                        <Typography
                          sx={{
                            fontWeight: 800,
                            color: "#0c1a2e",
                            fontSize: "0.95rem",
                            lineHeight: 1,
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                          }}
                        >
                          App Store
                        </Typography>
                      </Box>
                    </Paper>
                  </motion.div>

                  <motion.div variants={staggerItemSoft}>
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<FaDownload size={15} />}
                      onClick={handleDownloadClick}
                      disabled={loading}
                      target="_blank"
                      sx={{
                        borderRadius: "14px",
                        textTransform: "none",
                        fontFamily: "'Bricolage Grotesque', sans-serif",
                        fontWeight: 700,
                        fontSize: "0.96rem",
                        px: 4,
                        py: 1.8,
                        background: "linear-gradient(135deg, #059669, #10b981)",
                        boxShadow: "0 6px 22px rgba(5,150,105,0.32)",
                        "&:hover": {
                          background:
                            "linear-gradient(135deg, #047857, #059669)",
                          transform: "translateY(-4px)",
                          boxShadow: "0 14px 30px rgba(5,150,105,0.42)",
                        },
                        transition: "all 0.3s",
                      }}
                    >
                      {loading ? "Downloading…" : "Download server for offline Exam mode"}
                      
                    </Button>
                  </motion.div>
                </Stack>
              </MB>
            </Box>

            <SectionDivider color="#7c3aed" />

            {/* ══════════════ TRUSTED ══════════════ */}
            {students.length > 0 && (
              <Box sx={{ mb: 18, textAlign: "center" }}>
                <Stack alignItems="center" mb={7}>
                  <SectionChip label="Trusted By" color="#7c3aed" />
                  <WordReveal text="Leading Institutions" />
                  <motion.div
                    initial={{ width: 0 }}
                    whileInView={{ width: 70 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4, duration: 0.8 }}
                  >
                    <Box
                      sx={{
                        height: 4,
                        borderRadius: 99,
                        background: "linear-gradient(90deg, #7c3aed, #a78bfa)",
                        mx: "auto",
                        mt: 2,
                        mb: 2,
                      }}
                    />
                  </motion.div>
                  <Typography
                    sx={{
                      color: "#64748b",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: "1rem",
                    }}
                  >
                    {students.length}+ schools trust LiteUpper for digital
                    assessments
                  </Typography>
                </Stack>
                <MB
                  variants={scaleIn}
                  initial="hidden"
                  whileInView="show"
                  viewport={vp(0.12)}
                >
                  <AvatarGroup
                    max={isMobile ? 6 : 10}
                    sx={{
                      justifyContent: "center",
                      "& .MuiAvatar-root": {
                        border: "3px solid #fff",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
                        transition: "all 0.28s",
                        "&:hover": {
                          transform: "scale(1.22) translateY(-5px)",
                          zIndex: 10,
                          boxShadow: "0 10px 28px rgba(124,58,237,0.35)",
                        },
                      },
                    }}
                  >
                    {students
                      .filter(
                        (i) =>
                          i.profile_pic?.trim() &&
                          i.profile_pic !==
                            "https://res.cloudinary.com/dakxr6itl/image/upload/v1/images/profile_pics/profile1.png"
                      )
                      .map((inst, i) => (
                        <Tooltip key={i} title={inst.name || "School"} arrow>
                          <Avatar
                            src={inst.profile_pic}
                            alt={inst.name}
                            sx={{
                              width: { xs: 56, md: 78 },
                              height: { xs: 56, md: 78 },
                            }}
                          />
                        </Tooltip>
                      ))}
                  </AvatarGroup>
                </MB>
              </Box>
            )}

            <SectionDivider color="#ef4444" />

            {/* ══════════════ VIDEOS (custom thumbnail + modal) ══════════════ */}
            <Box sx={{ mb: 18 }}>
              <Stack alignItems="center" mb={9}>
                <SectionChip
                  label="Demo"
                  color="#ef4444"
                  bg="rgba(239,68,68,0.08)"
                />
                <WordReveal text="See LiteUpper in Action" />
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: 70 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                >
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 99,
                      background: "linear-gradient(90deg, #ef4444, #f97316)",
                      mx: "auto",
                      mt: 2,
                    }}
                  />
                </motion.div>
              </Stack>

              <MB
                variants={staggerContainer(0.16, 0.05)}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.05)}
              >
                <Grid container spacing={3}>
                  {VIDEOS.map((v, i) => (
                    <Grid item xs={12} md={6} key={v.title}>
                      <motion.div variants={staggerItem}>
                        <Box
                          onClick={() => setActiveVideo(v)}
                          sx={{
                            borderRadius: "22px",
                            overflow: "hidden",
                            border: "1px solid #e2e8f0",
                            cursor: "pointer",
                            position: "relative",
                            transition: "all 0.3s",
                            "&:hover": {
                              boxShadow: "0 24px 56px rgba(0,0,0,0.14)",
                              transform: "translateY(-8px)",
                              borderColor: "rgba(239,68,68,0.3)",
                              "& .play-overlay": { opacity: 1 },
                              "& .thumb-img": { transform: "scale(1.06)" },
                            },
                          }}
                        >
                          {/* Thumbnail */}
                          <Box
                            sx={{
                              position: "relative",
                              paddingTop: "56.25%",
                              overflow: "hidden",
                              background: "#0c1a2e",
                            }}
                          >
                            <Box
                              className="thumb-img"
                              component="img"
                              src={v.thumb}
                              alt={v.title}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                              sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                transition: "transform 0.5s ease",
                              }}
                            />
                            {/* Play overlay */}
                            <Box
                              className="play-overlay"
                              sx={{
                                position: "absolute",
                                inset: 0,
                                background: "rgba(0,0,0,0.45)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                opacity: 0,
                                transition: "opacity 0.28s ease",
                              }}
                            >
                              <Box
                                sx={{
                                  width: 64,
                                  height: 64,
                                  borderRadius: "50%",
                                  background: "rgba(239,68,68,0.92)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  boxShadow: "0 0 0 12px rgba(239,68,68,0.2)",
                                }}
                              >
                                <FaPlay
                                  size={22}
                                  color="#fff"
                                  style={{ marginLeft: 4 }}
                                />
                              </Box>
                            </Box>
                          </Box>
                          {/* Footer */}
                          <Box
                            sx={{
                              px: 3,
                              py: 2.5,
                              borderTop: "1px solid #e2e8f0",
                              background:
                                "linear-gradient(135deg, #f8fafc, #fff)",
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                            >
                              <Box
                                sx={{
                                  width: 34,
                                  height: 34,
                                  borderRadius: "11px",
                                  background: "rgba(239,68,68,0.1)",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                }}
                              >
                                <FaPlayCircle size={16} color="#ef4444" />
                              </Box>
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  color: "#0c1a2e",
                                  fontSize: "0.92rem",
                                  fontFamily:
                                    "'Bricolage Grotesque', sans-serif",
                                }}
                              >
                                {v.title}
                              </Typography>
                            </Stack>
                          </Box>
                        </Box>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </MB>
            </Box>

            <SectionDivider color="#059669" />

            {/* ══════════════ GOALS ══════════════ */}
            <Box sx={{ mb: 18 }}>
              <Stack alignItems="center" mb={9}>
                <SectionChip label="Our Goals" color="#059669" />
                <WordReveal text="What We Stand For" />
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: 70 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4, duration: 0.8 }}
                >
                  <Box
                    sx={{
                      height: 4,
                      borderRadius: 99,
                      background: "linear-gradient(90deg, #059669, #10b981)",
                      mx: "auto",
                      mt: 2,
                    }}
                  />
                </motion.div>
              </Stack>

              <MB
                variants={staggerContainer(0.14, 0.05)}
                initial="hidden"
                whileInView="show"
                viewport={vp(0.05)}
              >
                <Grid container spacing={3}>
                  {GOALS.map((goal, i) => (
                    <Grid item xs={12} sm={6} md={4} key={i}>
                      <motion.div
                        variants={staggerItemScale}
                        style={{ height: "100%" }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            p: 4,
                            borderRadius: "22px",
                            height: "100%",
                            background: goal.bg,
                            border: `1.5px solid ${goal.border}`,
                            position: "relative",
                            overflow: "hidden",
                            transition: "all 0.3s",
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: "-100%",
                              width: "60%",
                              height: "100%",
                              background: `linear-gradient(90deg,transparent,${goal.accent}14,transparent)`,
                              transition: "left 0.55s ease",
                            },
                            "&:hover": {
                              transform: "translateY(-8px)",
                              boxShadow: `0 20px 44px ${goal.accent}1e`,
                              borderColor: goal.accent,
                              "&::before": { left: "100%" },
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "14px",
                              background: "#fff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              mb: 3,
                              boxShadow: `0 6px 16px ${goal.accent}1a`,
                            }}
                          >
                            {goal.icon}
                          </Box>
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: "#0c1a2e",
                              lineHeight: 1.78,
                              fontSize: "0.93rem",
                              fontFamily: "'Bricolage Grotesque', sans-serif",
                            }}
                          >
                            {goal.text}
                          </Typography>
                        </Paper>
                      </motion.div>
                    </Grid>
                  ))}
                </Grid>
              </MB>
            </Box>

            {/* ══════════════ FUTURE VISION ══════════════ */}
            <MP
              variants={scaleIn}
              initial="hidden"
              whileInView="show"
              viewport={vp(0.1)}
              elevation={0}
              sx={{
                p: { xs: 7, md: 11 },
                borderRadius: "32px",
                mb: 14,
                background:
                  "linear-gradient(140deg, #010a1a 0%, #012d6b 35%, #0277bd 65%, #7c3aed 100%)",
                position: "relative",
                overflow: "hidden",
                textAlign: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0.04,
                  backgroundImage:
                    "radial-gradient(circle, #fff 1px, transparent 1px)",
                  backgroundSize: "34px 34px",
                }}
              />
              {/* Orbs */}
              <Box
                sx={{
                  position: "absolute",
                  top: -90,
                  right: -90,
                  width: 360,
                  height: 360,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.05)",
                  animation: "orbFloat 16s ease-in-out infinite",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -60,
                  left: -60,
                  width: 260,
                  height: 260,
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.03)",
                  animation: "orbFloat 20s ease-in-out infinite reverse",
                }}
              />

              <Box sx={{ position: "relative", zIndex: 1 }}>
                <MB
                  variants={heroReveal}
                  initial="hidden"
                  whileInView="show"
                  viewport={vp(0.2)}
                  transition={{ delay: 0.2 }}
                >
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: "50%",
                      background: "rgba(255,255,255,0.14)",
                      border: "1.5px solid rgba(255,255,255,0.24)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 4.5,
                      animation: "floatY 3.5s ease-in-out infinite",
                      boxShadow: "0 0 32px rgba(255,255,255,0.1)",
                    }}
                  >
                    <FaEye size={30} color="#fff" />
                  </Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      color: "#fff",
                      letterSpacing: "-1px",
                      mb: 4,
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: { xs: "2.2rem", md: "3rem" },
                    }}
                  >
                    Our Future Vision
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.78)",
                      maxWidth: 760,
                      mx: "auto",
                      lineHeight: 1.92,
                      fontSize: "1.08rem",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    At LiteUpper, we envision becoming the leading global
                    platform for exam preparation. We aim to expand with
                    interactive study tools, video tutorials, and real-time
                    quizzes. Our future includes advanced AI-driven analytics to
                    tailor study plans for every user — ensuring that students
                    worldwide have access to the best tools to succeed
                    academically.
                  </Typography>
                </MB>
              </Box>
            </MP>

            {/* ══════════════ CONTACT ══════════════ */}
            <MP
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={vp(0.1)}
              id="contact"
              elevation={0}
              sx={{
                p: { xs: 7, md: 11 },
                borderRadius: "32px",
                textAlign: "center",
                background: "#080f1e",
                border: "1px solid rgba(255,255,255,0.06)",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  opacity: 0.02,
                  backgroundImage:
                    "radial-gradient(circle, #fff 1px, transparent 1px)",
                  backgroundSize: "26px 26px",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  top: -70,
                  right: "18%",
                  width: 300,
                  height: 300,
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle, rgba(2,136,209,0.12) 0%, transparent 70%)",
                  animation: "orbFloat 13s ease-in-out infinite",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  bottom: -50,
                  left: "8%",
                  width: 220,
                  height: 220,
                  borderRadius: "50%",
                  background:
                    "radial-gradient(circle, rgba(124,58,237,0.1) 0%, transparent 70%)",
                  animation: "orbFloat 17s ease-in-out infinite reverse",
                }}
              />

              <Box sx={{ position: "relative", zIndex: 1 }}>
                <MB
                  variants={scaleIn}
                  initial="hidden"
                  whileInView="show"
                  viewport={vp(0.2)}
                  transition={{ delay: 0.12 }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      background:
                        "linear-gradient(135deg, rgba(2,136,209,0.28), rgba(14,165,233,0.18))",
                      border: "1.5px solid rgba(2,136,209,0.35)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 4.5,
                      animation: "floatY 3.5s ease-in-out infinite",
                      boxShadow: "0 0 32px rgba(2,136,209,0.15)",
                    }}
                  >
                    <FaHeadset size={32} color="#0ea5e9" />
                  </Box>
                </MB>

                <MB
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={vp(0.2)}
                  transition={{ delay: 0.28 }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 900,
                      color: "#fff",
                      mb: 1.5,
                      letterSpacing: "-1px",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontSize: { xs: "2.2rem", md: "3rem" },
                    }}
                  >
                    24/7 Customer Support
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255,255,255,0.42)",
                      mb: 6.5,
                      fontSize: "1.08rem",
                      fontFamily: "'Bricolage Grotesque', sans-serif",
                      fontWeight: 400,
                    }}
                  >
                    Have questions? Our team is always here to help.
                  </Typography>
                </MB>

                <MB
                  variants={staggerContainerFast(0.16, 0.45)}
                  initial="hidden"
                  whileInView="show"
                  viewport={vp(0.2)}
                >
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    justifyContent="center"
                    alignItems="center"
                  >
                    {[
                      {
                        label: "liteupper@gmail.com",
                        href: "mailto:liteupper@gmail.com",
                        icon: <FaEnvelope size={15} />,
                        sx: {
                          background: "#fff",
                          color: "#0288D1",
                          fontWeight: 800,
                          boxShadow: "0 6px 22px rgba(255,255,255,0.16)",
                          "&:hover": {
                            background: "#f0f9ff",
                            transform: "translateY(-4px)",
                            boxShadow: "0 14px 32px rgba(255,255,255,0.22)",
                          },
                        },
                      },
                      {
                        label: "Chat on WhatsApp",
                        href: "https://wa.me/2349061709128",
                        icon: <FaWhatsapp size={18} />,
                        target: "_blank",
                        sx: {
                          background:
                            "linear-gradient(135deg, #25D366, #128C7E)",
                          color: "#fff",
                          fontWeight: 700,
                          boxShadow: "0 6px 22px rgba(37,211,102,0.32)",
                          "&:hover": {
                            background:
                              "linear-gradient(135deg, #20bd5a, #0e7a6e)",
                            transform: "translateY(-4px)",
                            boxShadow: "0 14px 32px rgba(37,211,102,0.42)",
                          },
                        },
                      },
                    ].map((btn) => (
                      <motion.div key={btn.label} variants={staggerItemSoft}>
                        <Button
                          variant="contained"
                          size="large"
                          href={btn.href}
                          target={btn.target}
                          startIcon={btn.icon}
                          sx={{
                            borderRadius: "14px",
                            textTransform: "none",
                            fontFamily: "'Bricolage Grotesque', sans-serif",
                            fontSize: "0.98rem",
                            px: 4,
                            py: 1.9,
                            transition: "all 0.3s",
                            ...btn.sx,
                          }}
                        >
                          {btn.label}
                        </Button>
                      </motion.div>
                    ))}
                  </Stack>
                </MB>
              </Box>
            </MP>
          </Container>
        </Box>
        <ServerDialog
          open={open}
          setOpen={setOpen}
          // handleDownload={handleDownload}
        />

        {!loaderDone && <PageLoader onDone={handleDone} />}
        <BackToTop />
      </Box>
    </ThemeProvider>
  );
};

export default LandingPage;
